import { MoveResult, PlayerResult, RoomResult } from './api-dto';
import {
  MoveType,
  PromptAnswerUnits,
  PromptFormat,
  SideBetPrediction,
  isSideBetMoveType,
} from './constants';
import { scoreSideBets } from './side-bets';

export type AwardId =
  | 'mindReader'
  | 'biggestOutlier'
  | 'crowdFavorite'
  | 'mostPredictable';

export type GameAward = {
  id: AwardId;
  title: string;
  winnerGuids: string[];
  // Crowd Favorite: authors of the winning posters (for the phone recap).
  relatedGuids?: string[];
  // "for …" fragment naming a person or quoting game content.
  reason: string;
};

const MAX_QUOTE = 72;

const quote = (text: string): string => {
  const trimmed = text.trim();
  if (trimmed.length <= MAX_QUOTE) {
    return `"${trimmed}"`;
  }
  return `"${trimmed.slice(0, MAX_QUOTE - 1).trimEnd()}…"`;
};

const joinList = (items: string[]): string => {
  if (items.length <= 2) {
    return items.join(' and ');
  }
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
};

const formatAmount = (value: number, units?: PromptAnswerUnits): string => {
  const n = String(Math.round(value));
  switch (units) {
    case PromptAnswerUnits.USD:
      return `$${n}`;
    case PromptAnswerUnits.PERCENTAGE:
      return `${n}%`;
    case PromptAnswerUnits.FAHRENHEIT:
      return `${n}°`;
    case PromptAnswerUnits.NUMBER:
    case undefined:
      return n;
    default:
      return `${n} ${units}`;
  }
};

const playerName = (player?: PlayerResult): string | undefined => {
  const name = player?.user.name?.trim();
  return name || undefined;
};

const majorityByPrompt = (room: RoomResult): Record<string, string[]> => {
  const counts: Record<string, Record<string, number>> = {};
  for (const response of room.responses) {
    if (!counts[response.promptGuid]) {
      counts[response.promptGuid] = {};
    }
    const byText = counts[response.promptGuid];
    byText[response.text] = (byText[response.text] ?? 0) + 1;
  }

  const majority: Record<string, string[]> = {};
  for (const [promptGuid, byText] of Object.entries(counts)) {
    let max = 0;
    let answers: string[] = [];
    for (const [text, count] of Object.entries(byText)) {
      if (count > max) {
        max = count;
        answers = [text];
      } else if (count === max) {
        answers.push(text);
      }
    }
    majority[promptGuid] = answers;
  }
  return majority;
};

const wasMoveCorrect = (
  move: MoveResult,
  majority: Record<string, string[]>
): boolean => {
  if (move.type === MoveType.FREE_FACT_OR_FIB) {
    return Boolean(move.text && move.factOrFib?.author.user.guid === move.text);
  }
  if (move.type === MoveType.MC_READ_THE_ROOM) {
    return Boolean(move.text && majority[move.prompt.guid]?.includes(move.text));
  }
  return false;
};

const guidsAtMax = (
  room: RoomResult,
  counts: Record<string, number>,
  requirePositive: boolean
): string[] => {
  const max = Math.max(0, ...room.players.map(p => counts[p.user.guid] ?? 0));
  if (requirePositive && max <= 0) {
    return [];
  }
  return room.players
    .filter(p => (counts[p.user.guid] ?? 0) === max)
    .map(p => p.user.guid);
};

type SideBetHit = {
  bettorGuid: string;
  name: string;
  nail: boolean;
};

const mindReaderReason = (count: number, hit?: SideBetHit): string => {
  if (hit) {
    const call = hit.nail
      ? `${hit.name} would nail it`
      : `${hit.name} would whiff`;
    if (count <= 1) {
      return `for knowing ${call}`;
    }
    return `for calling ${count} side bets, including that ${call}`;
  }
  if (count <= 1) {
    return 'for calling a side bet';
  }
  return `for calling ${count} side bets`;
};

const mindReader = (room: RoomResult): GameAward | undefined => {
  const majority = majorityByPrompt(room);
  const counts: Record<string, number> = {};
  const hits: SideBetHit[] = [];
  let anyBets = false;

  for (const move of room.moves ?? []) {
    if (!move.submitted || !isSideBetMoveType(move.type)) {
      continue;
    }
    const bets = move.sideBets ?? [];
    if (bets.length === 0) {
      continue;
    }
    anyBets = true;
    const correct = wasMoveCorrect(move, majority);
    const { winners } = scoreSideBets(bets, correct);
    const name = playerName(move.player);
    for (const guid of winners) {
      counts[guid] = (counts[guid] ?? 0) + 1;
      const bet = bets.find(b => b.bettorGuid === guid);
      if (!name || !bet) {
        continue;
      }
      hits.push({
        bettorGuid: guid,
        name,
        nail: bet.prediction === SideBetPrediction.CORRECT,
      });
    }
  }

  if (!anyBets) {
    return undefined;
  }

  const winnerGuids = guidsAtMax(room, counts, true);
  if (winnerGuids.length === 0) {
    return undefined;
  }

  const count = counts[winnerGuids[0]] ?? 0;
  const hit = hits.find(h => h.bettorGuid === winnerGuids[0]);
  return {
    id: 'mindReader',
    title: 'Mind Reader',
    winnerGuids,
    reason: mindReaderReason(count, hit),
  };
};

const biggestOutlier = (room: RoomResult): GameAward | undefined => {
  const move = room.moves?.find(m => m.type === MoveType.SLIDER_ALL_PLAY);
  if (!move) {
    return undefined;
  }

  const answers: Record<string, number> = {};
  for (const response of room.responses) {
    if (response.promptGuid !== move.prompt.guid) {
      continue;
    }
    const value = parseInt(response.text, 10);
    if (Number.isNaN(value)) {
      continue;
    }
    answers[response.userGuid] = value;
  }

  const answered = room.players.filter(p => p.user.guid in answers);
  if (answered.length === 0) {
    return undefined;
  }

  const avg = answered.reduce((sum, p) => sum + answers[p.user.guid], 0) / answered.length;
  const diffs = answered.map(p => ({
    guid: p.user.guid,
    diff: Math.abs(answers[p.user.guid] - avg),
  }));
  const maxDiff = Math.max(...diffs.map(d => d.diff));
  if (maxDiff <= 0) {
    return undefined;
  }

  const winnerGuids = diffs.filter(d => d.diff === maxDiff).map(d => d.guid);
  const units = move.prompt.answer?.units;
  const guesses = [...new Set(winnerGuids.map(guid => formatAmount(answers[guid], units)))];
  return {
    id: 'biggestOutlier',
    title: 'Biggest Outlier',
    winnerGuids,
    reason: `for guessing ${joinList(guesses)} when the group averaged ${formatAmount(avg, units)}`,
  };
};

const crowdFavoriteReason = (
  maxVotes: number,
  stories: string[]
): string => {
  const voteWord = maxVotes === 1 ? 'vote' : 'votes';
  const unique = [...new Set(stories.map(s => s.trim()).filter(Boolean))];
  const quoted = unique.map(quote);
  if (quoted.length === 0) {
    return stories.length > 1
      ? `for ${maxVotes} ${voteWord} each`
      : `for ${maxVotes} ${voteWord}`;
  }
  if (stories.length > 1) {
    return `for ${maxVotes} ${voteWord} each on ${joinList(quoted)}`;
  }
  return `for ${maxVotes} ${voteWord} on ${quoted[0]}`;
};

const crowdFavorite = (room: RoomResult): GameAward | undefined => {
  const move = room.moves?.find(m => m.type === MoveType.DRAWN_TOGETHER);
  if (!move) {
    return undefined;
  }

  const assignments = move.drawingAssignments ?? [];
  const maxVotes = assignments.reduce((max, da) => Math.max(max, da.voterGuids.length), 0);
  if (maxVotes === 0) {
    return undefined;
  }

  const winners = assignments.filter(da => da.voterGuids.length === maxVotes);
  return {
    id: 'crowdFavorite',
    title: 'Crowd Favorite',
    winnerGuids: winners.map(w => w.drawer.user.guid),
    relatedGuids: winners.map(w => w.responseToDraw.userGuid),
    reason: crowdFavoriteReason(
      maxVotes,
      winners.map(w => w.responseToDraw.text)
    ),
  };
};

const mostPredictableReason = (
  times: number,
  example?: string
): string => {
  const including = example?.trim() ? `, including ${quote(example)}` : '';
  if (times <= 1) {
    return `for picking with the room${including}`;
  }
  return `for picking with the room ${times} times${including}`;
};

const mostPredictable = (room: RoomResult): GameAward | undefined => {
  const mcPrompts = room.prompts.filter(p => p.type === PromptFormat.MULTIPLE_CHOICE);
  if (mcPrompts.length === 0) {
    return undefined;
  }

  const majority = majorityByPrompt(room);
  const counts: Record<string, number> = {};
  const examples: Record<string, string> = {};
  let anyResponses = false;

  for (const prompt of mcPrompts) {
    const answers = majority[prompt.guid] ?? [];
    if (answers.length === 0) {
      continue;
    }
    for (const response of room.responses) {
      if (response.promptGuid !== prompt.guid) {
        continue;
      }
      anyResponses = true;
      if (answers.includes(response.text)) {
        counts[response.userGuid] = (counts[response.userGuid] ?? 0) + 1;
        if (!examples[response.userGuid] && response.text.trim()) {
          examples[response.userGuid] = response.text;
        }
      }
    }
  }

  if (!anyResponses) {
    return undefined;
  }

  const playerCounts = room.players.map(p => counts[p.user.guid] ?? 0);
  const max = Math.max(...playerCounts);
  const min = Math.min(...playerCounts);
  if (max <= 0 || min === max) {
    return undefined;
  }

  const winnerGuids = guidsAtMax(room, counts, true);
  return {
    id: 'mostPredictable',
    title: 'Most Predictable',
    winnerGuids,
    reason: mostPredictableReason(
      counts[winnerGuids[0]] ?? 0,
      examples[winnerGuids[0]]
    ),
  };
};

/**
 * End-of-game awards derived from a RoomResult snapshot. Missing rounds,
 * empty votes, and "everybody tied" cases are omitted.
 */
export const getGameAwards = (room: RoomResult): GameAward[] => (
  [mindReader(room), biggestOutlier(room), crowdFavorite(room), mostPredictable(room)]
    .filter((award): award is GameAward => Boolean(award))
);

const firstCorrectGuess = (room: RoomResult, playerGuid: string): MoveType | undefined => {
  const majority = majorityByPrompt(room);
  for (const move of room.moves ?? []) {
    if (!move.submitted || !isSideBetMoveType(move.type)) {
      continue;
    }
    if (move.player?.user.guid !== playerGuid) {
      continue;
    }
    if (wasMoveCorrect(move, majority)) {
      return move.type;
    }
  }
  return undefined;
};

const awardLine = (award: GameAward): string => `${award.title}: ${award.reason}`;

/**
 * One-liner for the phone recap. First match wins: Crowd Favorite (drawer
 * then story author), Mind Reader, Biggest Outlier, a correct team-round
 * guess, then a fallback.
 */
export const getPersonalBestMoment = (room: RoomResult, playerGuid: string): string => {
  const awards = getGameAwards(room);
  const crowd = awards.find(a => a.id === 'crowdFavorite');
  if (crowd?.winnerGuids.includes(playerGuid)) {
    return awardLine(crowd);
  }
  if (crowd?.relatedGuids?.includes(playerGuid)) {
    return awardLine(crowd);
  }
  const mind = awards.find(a => a.id === 'mindReader' && a.winnerGuids.includes(playerGuid));
  if (mind) {
    return awardLine(mind);
  }
  const outlier = awards.find(a => a.id === 'biggestOutlier' && a.winnerGuids.includes(playerGuid));
  if (outlier) {
    return awardLine(outlier);
  }

  const correct = firstCorrectGuess(room, playerGuid);
  if (correct === MoveType.MC_READ_THE_ROOM) {
    return 'You nailed Read the Room.';
  }
  if (correct === MoveType.FREE_FACT_OR_FIB) {
    return 'You nailed Fact or Fib.';
  }

  return 'You showed up.';
};
